import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Loader2, Zap, Clock, Lightbulb, CheckCircle, TrendingUp, Flame } from 'lucide-react';
import { Button } from './ui/button';
import { trackExampleIdeaClicked } from '../utils/analytics';

// Trending app ideas - will be replaceable with dynamic data later
const TRENDING_IDEAS = [
    { label: 'AI fitness coach', idea: 'Build an AI fitness coach that creates personalized workout plans, tracks progress, and adapts based on performance and recovery data' },
    { label: 'AI tattoo stencil generator', idea: 'Build an AI tattoo stencil generator that creates custom designs from descriptions and shows realistic placement previews' },
    { label: 'Kids learning app', idea: 'Build an engaging kids learning app with interactive lessons, progress tracking, and rewards that adapts to each child\'s pace' },
    { label: 'AI business planner', idea: 'Build an AI business planner that helps validate ideas, create business plans, and identify market opportunities' },
    { label: 'AI content creator', idea: 'Build an AI content creator that transforms ideas into social posts, blog articles, and video scripts for multiple platforms' },
    { label: 'AI dating assistant', idea: 'Build an AI dating assistant that helps write engaging profiles, suggests conversation starters, and gives date planning advice' },
    { label: 'AI voice agent', idea: 'Build an AI voice agent that handles phone calls, schedules appointments, and answers customer questions 24/7' },
    { label: 'AI resume optimizer', idea: 'Build an AI resume optimizer that tailors resumes to job descriptions and provides real-time improvement suggestions' },
    { label: 'AI meal planner', idea: 'Build an AI meal planner that creates personalized recipes, generates grocery lists, and tracks nutrition goals' },
];

// Rotating placeholder examples
const PLACEHOLDER_EXAMPLES = [
    "AI fitness coach that creates workout plans and tracks your progress",
    "Kids learning app with interactive lessons and progress rewards",
    "AI business planner that validates ideas and finds market opportunities",
    "AI content creator for social posts, blogs, and video scripts",
    "AI voice agent that handles calls and schedules appointments",
    "Personal finance app that analyzes spending and suggests savings goals",
];

// Number of ideas to show at a time
const IDEAS_PER_VIEW = 3;

export const ChatInput = ({ onSubmit, isLoading, disabled, showExamples = true }) => {
    const [idea, setIdea] = useState('');
    const [placeholderIndex, setPlaceholderIndex] = useState(0);
    const [trendingStartIndex, setTrendingStartIndex] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);

    // Rotate placeholder every 4 seconds
    useEffect(() => {
        if (!idea) {
            const interval = setInterval(() => {
                setPlaceholderIndex((prev) => (prev + 1) % PLACEHOLDER_EXAMPLES.length);
            }, 4000);
            return () => clearInterval(interval);
        }
    }, [idea]);

    // Rotate trending ideas every 4 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            setIsTransitioning(true);
            setTimeout(() => {
                setTrendingStartIndex((prev) => (prev + IDEAS_PER_VIEW) % TRENDING_IDEAS.length);
                setIsTransitioning(false);
            }, 300); // Match fade out duration
        }, 4000);
        return () => clearInterval(interval);
    }, []);

    const currentPlaceholder = `Example: ${PLACEHOLDER_EXAMPLES[placeholderIndex]}`;

    // Get current visible trending ideas
    const getVisibleIdeas = useCallback(() => {
        const ideas = [];
        for (let i = 0; i < IDEAS_PER_VIEW; i++) {
            const index = (trendingStartIndex + i) % TRENDING_IDEAS.length;
            ideas.push(TRENDING_IDEAS[index]);
        }
        return ideas;
    }, [trendingStartIndex]);

    const visibleIdeas = getVisibleIdeas();

    const handleSubmit = (e) => {
        e.preventDefault();
        if (idea.trim() && !isLoading && !disabled) {
            onSubmit(idea.trim());
        }
    };

    const handleExampleClick = (exampleLabel, exampleIdea) => {
        setIdea(exampleIdea);
        trackExampleIdeaClicked(exampleLabel);
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="w-full"
        >
            {/* Value statement - moved above prompt */}
            {showExamples && (
                <p className="text-center text-sm text-zinc-400 mb-4">
                    <Clock className="w-4 h-4 inline mr-1.5 text-violet-400" />
                    Turn one idea into a full app blueprint, build plan, and execution path.
                </p>
            )}

            {/* Main Prompt Box */}
            <form onSubmit={handleSubmit} className="relative mb-4">
                <div 
                    className="bg-zinc-900/80 border border-zinc-700 rounded-2xl p-4 focus-within:border-violet-500/50 transition-all duration-300"
                    style={{
                        boxShadow: '0 0 30px rgba(124, 58, 237, 0.15)',
                    }}
                >
                    <textarea
                        data-testid="idea-input"
                        value={idea}
                        onChange={(e) => setIdea(e.target.value)}
                        placeholder={currentPlaceholder}
                        disabled={isLoading || disabled}
                        className="w-full bg-transparent border-0 outline-none resize-none text-white placeholder:text-zinc-500 text-base md:text-lg leading-relaxed min-h-[80px] mb-3"
                        rows={3}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSubmit(e);
                            }
                        }}
                        onFocus={(e) => {
                            // Prevent scroll on focus
                            e.target.style.scrollMargin = '0';
                        }}
                    />
                    <div className="flex items-center justify-between">
                        <p className="text-xs text-zinc-500 hidden sm:block">
                            Press Enter to generate
                        </p>
                        <Button
                            data-testid="generate-blueprint-btn"
                            type="submit"
                            disabled={!idea.trim() || isLoading || disabled}
                            className="bg-violet-600 hover:bg-violet-500 text-white rounded-xl px-6 py-2.5 font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ml-auto"
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Generating...
                                </>
                            ) : (
                                <>
                                    <Zap className="w-4 h-4 mr-2" />
                                    Generate My App Blueprint
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </form>

            {/* Trending Ideas - Rotating */}
            {showExamples && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    className="mb-12"
                >
                    <p className="text-sm text-zinc-400 mb-3 flex items-center gap-2">
                        <Flame className="w-4 h-4 text-orange-400" />
                        <span className="font-medium text-orange-300/90">Popular app ideas right now</span>
                    </p>
                    <div className="flex flex-wrap gap-2 min-h-[40px]">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={trendingStartIndex}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.3 }}
                                className="flex flex-wrap gap-2"
                            >
                                {visibleIdeas.map((example, index) => (
                                    <button
                                        key={`${trendingStartIndex}-${index}`}
                                        data-testid={`trending-idea-${index}`}
                                        onClick={() => handleExampleClick(example.label, example.idea)}
                                        className="px-3 py-1.5 text-sm bg-gradient-to-r from-orange-500/10 to-amber-500/10 border border-orange-500/30 rounded-lg text-zinc-200 hover:from-orange-500/20 hover:to-amber-500/20 hover:border-orange-400/50 hover:text-white hover:shadow-[0_0_15px_rgba(249,115,22,0.25)] transition-all duration-200 hover:scale-[1.03]"
                                    >
                                        {example.label}
                                    </button>
                                ))}
                            </motion.div>
                        </AnimatePresence>
                        {/* Progress indicator dots */}
                        <div className="w-full flex justify-center gap-1 mt-2">
                            {Array.from({ length: Math.ceil(TRENDING_IDEAS.length / IDEAS_PER_VIEW) }).map((_, i) => (
                                <div
                                    key={i}
                                    className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                                        Math.floor(trendingStartIndex / IDEAS_PER_VIEW) === i
                                            ? 'bg-orange-400 w-3'
                                            : 'bg-zinc-700'
                                    }`}
                                />
                            ))}
                        </div>
                    </div>
                </motion.div>
            )}

            {/* What is Vibe Coding Section */}
            {showExamples && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="grid md:grid-cols-2 gap-6 mb-12"
                >
                    {/* What is Vibe Coding? */}
                    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
                        <h3 className="text-lg font-semibold text-white mb-3">What is Vibe Coding?</h3>
                        <p className="text-zinc-400 text-sm leading-relaxed mb-4">
                            Vibe coding means describing what you want to build and letting AI generate the structure for you.
                            Instead of writing complicated code, you simply explain your idea.
                        </p>
                        <p className="text-zinc-400 text-sm mb-3">Vibe Coding Coach creates:</p>
                        <ul className="space-y-2">
                            {[
                                'App blueprint & features',
                                'System architecture',
                                'Recommended tech stack',
                                'Monetization ideas',
                                'Launch strategy'
                            ].map((item, i) => (
                                <li key={i} className="flex items-center gap-2 text-sm text-zinc-300">
                                    <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Why Use Vibe Coding Coach? */}
                    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
                        <h3 className="text-lg font-semibold text-white mb-3">Why Use Vibe Coding Coach?</h3>
                        <p className="text-zinc-400 text-sm leading-relaxed mb-4">
                            Building apps with AI normally requires dozens of prompts and wasted credits.
                        </p>
                        <p className="text-zinc-400 text-sm leading-relaxed mb-4">
                            Vibe Coding Coach generates the entire app plan at once so you can start building immediately.
                        </p>
                        <div className="bg-violet-500/10 border border-violet-500/20 rounded-lg p-4 mt-4">
                            <p className="text-violet-300 text-sm font-medium">
                                Turn one idea into a full app blueprint, build plan, and execution path.
                            </p>
                        </div>
                    </div>
                </motion.div>
            )}
        </motion.div>
    );
};
